import { LogoMark, cx } from '@flexbyt/widget-preview';
import type { ReactNode } from 'react';

/**
 * The two kinds of window the homepage draws, and why there are exactly two.
 *
 * ===========================================================================
 * The problem this solves
 * ===========================================================================
 * Import, Manage and Display are three sections that each show a rectangle
 * full of reviews, and they were drawn almost identically — a hairline, a
 * radius, a shadow, some rows. Read one at a time each was fine. Read in
 * sequence, which is the only way anybody reads them, they raised a question
 * the page never answered: *am I looking at the same screen three times?*
 *
 * The answer is no, and it is the single most important thing the middle of
 * this page has to communicate, because it is the product's whole shape:
 *
 *   Import   ReviewsJet   — the app, listing what you have connected
 *   Manage   ReviewsJet   — the app, the library you moderate
 *   Display  your website — not the app at all
 *
 * Two of those are one place and the third is somewhere else entirely, and a
 * visitor who does not grasp that thinks ReviewsJet is a website builder, or
 * that their reviews live on reviewsjet.com. So the app screens now carry the
 * ReviewsJet mark and a screen name in a title bar, and the website carries a
 * browser's address bar with somebody else's domain in it. You can tell them
 * apart from across the room, which is the bar a diagram has to clear.
 *
 * ===========================================================================
 * Why the chrome is a component rather than markup in three files
 * ===========================================================================
 * Because the distinction only works if it is applied consistently, and three
 * hand-drawn title bars drift within a week — one gets a different radius, one
 * forgets the mark, and the pattern that was carrying the meaning stops
 * carrying it. There is one of each here, and the sections choose which they
 * are.
 */

/**
 * A ReviewsJet screen.
 *
 * The mark is the point: it is the same logo as the one in the site header
 * twenty pixels up the page, which is what says "this is our software" without
 * a caption saying so.
 */
export function AppPanel({
  screen,
  meta,
  children,
  className,
}: {
  /** The name of the screen, as the dashboard's own sidebar would say it. */
  screen: string;
  /** A count, a status — the quiet line on the right of the title bar. */
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'overflow-hidden rounded-[1.5rem] bg-white shadow-[0_1px_2px_rgba(10,10,11,0.05)] ring-1 ring-black/[0.07]',
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-black/[0.06] bg-surface/70 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <LogoMark className="h-4 shrink-0 text-ink-950" />
          {/*
            A rule between the mark and the screen name, the way an application
            title bar separates the product from the page. Without it the two
            read as one phrase — "ReviewsJet Review library" — rather than as
            "ReviewsJet, showing: Review library".
          */}
          <span aria-hidden className="h-4 w-px bg-ink-200" />
          <p className="text-body-sm font-semibold text-ink-950">{screen}</p>
        </div>
        {meta ? (
          <p className="nums text-caption text-ink-500">{meta}</p>
        ) : null}
      </div>

      {children}
    </div>
  );
}

/**
 * Somebody else's website, with ReviewsJet nowhere in sight.
 *
 * ===========================================================================
 * The address bar is doing the work
 * ===========================================================================
 * This used to be the site's own header alone — a wordmark and four nav items
 * — which reads as *a website* to a designer and as *another panel* to
 * everybody else. An address bar is the one piece of furniture that is
 * unambiguous: it appears around web pages and around nothing else, and the
 * domain in it is visibly not reviewsjet.com.
 *
 * It is deliberately minimal — three dots, a rounded field, a padlock. A
 * faithful browser chrome (tabs, a back button, a bookmark star) would be a
 * drawing competing with the widget inside it, and the widget is the subject.
 */
export function SitePanel({
  domain,
  name,
  nav,
  cta,
  children,
  className,
}: {
  /** What appears in the address bar. A plausible customer, never a real one. */
  domain: string;
  /** The business's own wordmark, in its own header. */
  name: string;
  /** The site's navigation. The last item is drawn as its button. */
  nav: string[];
  /** The label on that button. */
  cta: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        /*
          The one place on this page where a shadow is more than a hairline. It
          is doing real work: this is a *different website*, sitting on top of
          ReviewsJet's, and the lift is what separates the two.
        */
        'overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-ink-150',
        className
      )}
    >
      {/* Browser chrome. Decoration for a screen reader — `aria-hidden`. */}
      <div
        aria-hidden
        className="flex items-center gap-3 border-b border-ink-150 bg-ink-50 px-4 py-2.5"
      >
        <div className="flex shrink-0 gap-1.5">
          {[0, 1, 2].map((dot) => (
            <span key={dot} className="size-2.5 rounded-full bg-ink-200" />
          ))}
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md bg-white px-2.5 py-1 ring-1 ring-ink-150">
          <LockIcon />
          <span className="truncate text-micro text-ink-500">{domain}</span>
        </div>
      </div>

      {/* The host site's own header. */}
      <div className="flex items-center justify-between gap-6 border-b border-ink-100 px-6 py-5 sm:px-10">
        <p className="text-title tracking-tight text-ink-950">{name}</p>
        <nav aria-hidden className="hidden items-center gap-7 sm:flex">
          {nav.map((item) => (
            <span key={item} className="text-caption text-ink-500">
              {item}
            </span>
          ))}
          <span className="rounded-md bg-ink-950 px-3.5 py-1.5 text-caption font-medium text-white">
            {cta}
          </span>
        </nav>
      </div>

      {children}

      {/* A hint of page below, so the widget reads as part of a site. */}
      <div className="border-t border-ink-100 px-6 py-7 sm:px-10">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="h-1.5 w-20 rounded-full bg-ink-150" />
          <div className="h-1.5 w-16 rounded-full bg-ink-100" />
          <div className="h-1.5 w-24 rounded-full bg-ink-100" />
          <div className="ml-auto h-1.5 w-28 rounded-full bg-ink-100" />
        </div>
      </div>
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      className="size-3 shrink-0 text-ink-400"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      aria-hidden
    >
      <rect x="2.5" y="5.2" width="7" height="5" rx="1.2" />
      <path d="M4.2 5.2V3.9a1.8 1.8 0 0 1 3.6 0v1.3" />
    </svg>
  );
}
